export const HERO_CTA_LABEL = 'Scan my skin — free';
export const HERO_CTA_TO = '/register';

export type StackItem = {
  pill: string;
  title: string;
  body: string;
  variant?: 'sand' | 'deep' | 'default';
};

export const PROBLEM_CARDS: StackItem[] = [
  {
    pill: 'labels',
    title: 'too many ingredients',
    body: 'niacinamide, retinaldehyde, panthenol. most labels read like a chemistry exam — and the ones that don’t are usually hiding something.',
    variant: 'sand',
  },
  {
    pill: 'noise',
    title: 'marketing over evidence',
    body: '“clinically proven” almost never means what you think. we surface what the ingredients actually are — not what the box says.',
    variant: 'default',
  },
  {
    pill: 'not your face',
    title: 'one-size-fits-no-one',
    body: 'skin is hyper-personal. generic rankings miss the only person who matters: you.',
    variant: 'deep',
  },
];

export type VideoStep = {
  pill: string;
  title: string;
  body: string;
  alpha?: boolean;
};

export const VIDEO_STEPS: VideoStep[] = [
  {
    pill: "hair analysis",
    title: "your hair, read from a single photo.",
    body: "kivo reads your hair type, texture, density and condition from one photo — then recommends the cuts, colors and styles that actually suit you, with care tips tailored to what it finds.",
  },
  {
    pill: "premium face scan",
    title: "the full picture of your skin.",
    body: "two AI models run simultaneously on one photo — skin type, visible patterns, under-eye area, warm tones, expression lines and more. then kivo builds a personalized am/pm routine around exactly what it finds.",
  },
  {
    pill: "makeup analysis",
    title: "colors made for your undertone.",
    body: "kivo reads your natural undertone and features to build a personalized makeup palette — the shades, looks and products that flatter you, not a generic seasonal chart.",
  },
  {
    pill: "product scan",
    title: "any product, decoded before you buy.",
    body: "scan a barcode — or snap the label — and kivo pulls the full ingredient list, then scores it against your skin: what's safe, what might irritate, and why. an honest read on the shelf, in seconds.",
  },
];

export type Stat = { num: string; label: string; context: string };

export const STATS: Stat[] = [
  {
    num: "200+",
    label: "chemistry rules behind every product pairing",
    context:
      "pH conflicts, ionic clashes, oxidation issues, photosensitivity notes — 200+ formulation rules run every time you combine two products, for a clear cosmetic read on how they work together.",
  },
  {
    num: "0",
    label: "paid placements. ever.",
    context:
      "kivo doesn't take money to rank one product over another. what you see is what the ingredients say — not what a brand paid for.",
  },
  {
    num: "<2 sec",
    label: "from photo to full skin read",
    context:
      "two AI models run in parallel on a single photo. skin type, visible patterns, plain-english output — done before you put your phone down.",
  },
];

export type Feature = {
  icon: string;
  title: string;
  body: string;
  alpha?: boolean;
};

export const FEATURES: Feature[] = [
  {
    icon: '✸',
    title: 'skin read in one scan',
    body: 'tone, texture, warm tones, pores, expression lines, dryness, and more — all from a single photo.',
  },
  {
    icon: '◯',
    title: 'plain-english reads',
    body: 'no jargon. no marketing. what’s happening on your skin, in a sentence.',
  },
  {
    icon: '⟶',
    title: 'product compatibility',
    body: 'scan a barcode or its label. see whether it actually works with the rest of your routine — and your skin.',
  },
  {
    icon: '◐',
    title: 'personal to your skin',
    body: 'adjusts for sensitivity, areas you want to treat gently, and your ethical preferences.',
  },
  {
    icon: '◇',
    title: 'yours to delete',
    body: 'encrypted in transit. processed securely. deletable in one tap. never sold.',
  },
];

export type PrivacyCard = { title: string; body: string };

export const PRIVACY_CARDS: PrivacyCard[] = [
  {
    title: 'your face stays yours.',
    body: 'scans are encrypted in transit, processed in our secure cloud, and deletable in one tap. never sold. never trained on without consent.',
  },
  {
    title: 'built to be explained.',
    body: 'every score traces back to the actual ingredients and signals behind it. no black box. no influencer rankings.',
  },
  {
    title: 'not medical advice.',
    body: 'kivo is a cosmetic tool, not a diagnostic one. For anything about your skin that worries you, please consult a qualified professional.',
  },
];

export type Billing = 'monthly' | 'yearly';

export type PriceTier = {
  name: string;
  monthly: string;
  yearly: string;
  yearlyAnnualNote?: string;
  desc: string;
  bullets: string[];
  cta: string;
  to: string;
  variant: 'default' | 'deep' | 'sand';
  popular?: boolean;
};

export const PRICE_TIERS: PriceTier[] = [
  {
    name: 'Free',
    monthly: '$0',
    yearly: '$0',
    desc: 'start with clarity.',
    bullets: ['1 scan / month', 'plain-english reads', 'routine starter'],
    cta: HERO_CTA_LABEL,
    to: HERO_CTA_TO,
    variant: 'default',
  },
  {
    name: 'Plus',
    monthly: '$9.99 / mo',
    yearly: '$79 / yr',
    yearlyAnnualNote: 'save 33%',
    desc: 'when you want depth.',
    bullets: [
      'everything in free',
      'unlimited scans',
      'full appearance read',
      'personalized routine',
      'weekly appearance snapshots',
    ],
    cta: 'start plus',
    to: HERO_CTA_TO,
    variant: 'deep',
    popular: true,
  },
  {
    name: 'Pro',
    monthly: '$14.99 / mo',
    yearly: '$119 / yr',
    desc: 'for full transparency.',
    bullets: [
      'everything in plus',
      'ingredient deep-dives',
      'routine simulator',
      'priority model access',
    ],
    cta: 'start pro',
    to: HERO_CTA_TO,
    variant: 'sand',
  },
];

export type FaqItem = { q: string; a: string };

export const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'how accurate is kivo?',
    a: 'we don’t publish a single accuracy number, because skin is too varied for one to be honest. instead, every score we show traces to the specific signals and ingredients behind it — so you can see exactly why we said what we said.',
  },
  {
    q: 'is this medical advice?',
    a: 'no. kivo is a cosmetic tool, not a diagnostic one. For anything about your skin that worries you, please consult a qualified professional.',
  },
  {
    q: 'what happens to my scan photos?',
    a: 'your scan is encrypted in transit and processed in our secure cloud. photos are kept while your account is active — you can delete them anytime in one tap, and they’re removed within 30 days of your request. we don’t sell them, and we don’t train third-party models on your face.',
  },
  {
    q: 'can i cancel anytime?',
    a: 'yes. one tap, no email gymnastics. see our Refund Policy for details.',
  },
  {
    q: 'do you sell products?',
    a: 'no. kivo reads what’s on the label — we don’t take payment to rank one product over another.',
  },
  {
    q: 'what devices does this work on?',
    a: 'any modern phone (iOS 16+, Android 10+) and most laptops. scans are processed in our secure cloud either way, so older devices work too.',
  },
];

export type FooterLink = { href: string; label: string };

export const FOOTER_LINKS: FooterLink[] = [
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
  { href: '/cookies', label: 'Cookie Policy' },
  { href: '/data-retention', label: 'Data Retention' },
  { href: '/refund', label: 'Refund Policy' },
  { href: '/ai-disclaimer', label: 'AI Disclaimer' },
  { href: '/photo-consent', label: 'Biometric Consent' },
  { href: '/affiliate-disclosure', label: 'Affiliate Disclosure' },
  { href: '/public-support', label: 'Support' },
];
